// src/services/hrServiceNew.js
import { 
  createService, 
  createFileUploadConfig, 
  createNestedCollection 
} from './factory';
import { api } from './api';

// Create base services for each HR module using the factory pattern

// Employee Service
const employeeServiceBase = createService('Employee', '/hr/employees', {
  fileUploadOptions: createFileUploadConfig('documents', 'documents'),
  nestedCollections: [
    createNestedCollection('documents', 'document'),
    createNestedCollection('leaves', 'leave'),
    createNestedCollection('performance', 'review')
  ],
  customEndpoints: {
    // Employee specific endpoints
    getByUserId: async (userId) => api.get(`/hr/employees/user/${userId}`),
    exportEmployees: async (params = {}) => api.get('/hr/employees/export', { params }),
    importEmployees: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/hr/employees/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    bulkUpdate: async (updates) => api.post('/hr/employees/bulk-update', updates),
    getOrgHierarchy: async () => api.get('/hr/org/hierarchy')
  }
});

// Leave Service
const leaveServiceBase = createService('Leave', '/leave/requests', {
  customEndpoints: {
    // Leave status operations
    submitLeave: async (id) => api.post(`/leave/requests/${id}/submit`),
    approveLeave: async (id, data) => api.post(`/leave/requests/${id}/approve`, data),
    rejectLeave: async (id, data) => api.post(`/leave/requests/${id}/reject`, data),
    cancelLeave: async (id, data) => api.post(`/leave/requests/${id}/cancel`, data),
    
    // Leave balance operations
    getLeaveBalance: async (employeeId) => api.get(`/leave/balance/${employeeId}`),
    getMyLeaveBalance: async () => api.get('/leave/my-balance'),
    updateLeaveBalance: async (employeeId, data) => api.put(`/leave/balance/${employeeId}/adjust`, data),
    
    // Employee self-service
    getMyLeaves: async (params) => api.get('/leave/my-leaves', { params }),
    createMyLeaveRequest: async (data) => api.post('/leave/my-request', data),
    
    // Leave configuration
    getLeaveTypes: async () => api.get('/leave/types'),
    getLeaveCalendar: async (params) => api.get('/leave/calendar', { params }),
    
    // Leave reports
    getLeaveReport: async (params) => api.get('/leave/reports/summary', { params }),
    getPendingApprovals: async () => api.get('/leave/approvals/pending')
  }
});

// Attendance Service
const attendanceServiceBase = createService('Attendance', '/attendance/records', {
  customEndpoints: {
    // Clock operations
    getCurrentStatus: async () => api.get('/attendance/status'),
    clockIn: async (data) => api.post('/attendance/clock-in', data),
    clockOut: async (data) => api.post('/attendance/clock-out', data),
    startBreak: async (data) => api.post('/attendance/break/start', data),
    endBreak: async (data) => api.post('/attendance/break/end', data),
    
    // Employee self-service
    getMyAttendance: async (params) => api.get('/attendance/my-attendance', { params }),
    getMyAttendanceSummary: async (params) => api.get('/attendance/my-attendance/summary', { params }),
    getMyTimesheet: async (params) => api.get('/attendance/timesheet', { params }),
    submitTimesheet: async (data) => api.post('/attendance/timesheet/submit', data),
    
    // Reports and analytics
    getTodayOverview: async () => api.get('/attendance/today-overview'),
    getDailyReport: async (params) => api.get('/attendance/reports/daily', { params }),
    getMonthlyReport: async (params) => api.get('/attendance/reports/monthly', { params }),
    getTeamAttendance: async (params) => api.get('/attendance/team', { params }),
    exportAttendance: async (params) => api.get('/attendance/export', { params }),
    
    // Bulk operations
    bulkClockIn: async (data) => api.post('/attendance/bulk-import', data),
    validateAttendance: async (ids) => api.post('/attendance/validate', { ids }),
    syncWithPayroll: async (data) => api.post('/attendance/sync-payroll', data)
  }
});

// Performance Service
const performanceServiceBase = createService('Performance', '/performance/reviews', {
  nestedCollections: [
    createNestedCollection('goals', 'goal'),
    createNestedCollection('feedback', 'feedback')
  ],
  customEndpoints: {
    // Review process
    submitSelfReview: async (id, data) => api.post(`/performance/reviews/${id}/self-review`, data),
    submitManagerReview: async (id, data) => api.post(`/performance/reviews/${id}/manager-review`, data),
    completeReview: async (id) => api.post(`/performance/reviews/${id}/complete`),
    acknowledgeReview: async (id) => api.post(`/performance/reviews/${id}/acknowledge`),
    
    // Goals management
    getGoals: async (params) => api.get('/performance/goals', { params }),
    getGoal: async (id) => api.get(`/performance/goals/${id}`),
    createGoal: async (data) => api.post('/performance/goals', data),
    updateGoal: async (id, data) => api.put(`/performance/goals/${id}`, data),
    updateGoalProgress: async (id, progress) => api.put(`/performance/goals/${id}/progress`, { progress }),
    
    // Analytics
    getPerformanceAnalytics: async (params) => api.get('/performance/analytics', { params }),
    getTeamPerformance: async (managerId, params) => api.get(`/performance/team/${managerId}`, { params }),
    
    // Templates and cycles
    getReviewTemplates: async () => api.get('/performance/templates'),
    getReviewCycles: async () => api.get('/performance/cycles'),
    createReviewCycle: async (data) => api.post('/performance/cycles', data)
  }
});

// Recruitment Service
const recruitmentServiceBase = createService('Recruitment', '/recruitment/jobs', {
  nestedCollections: [
    createNestedCollection('applications', 'application'),
    createNestedCollection('candidates', 'candidate')
  ],
  customEndpoints: {
    // Job posting operations
    publishJob: async (id) => api.post(`/recruitment/jobs/${id}/publish`),
    archiveJob: async (id) => api.post(`/recruitment/jobs/${id}/archive`),
    
    // Candidate management
    getCandidates: async (params) => api.get('/recruitment/candidates', { params }),
    getCandidate: async (id) => api.get(`/recruitment/candidates/${id}`),
    createCandidate: async (data) => api.post('/recruitment/candidates', data),
    updateCandidate: async (id, data) => api.put(`/recruitment/candidates/${id}`, data),
    
    // Application process
    updateApplicationStatus: async (candidateId, jobId, status) => 
      api.put(`/recruitment/candidates/${candidateId}/applications/${jobId}/status`, { status }),
    scheduleInterview: async (data) => api.post('/recruitment/interviews/schedule', data),
    createOffer: async (data) => api.post('/recruitment/offers', data),
    
    // Bulk operations
    bulkCandidateActions: async (actions) => api.post('/recruitment/candidates/bulk-actions', actions),
    
    // Analytics
    getRecruitmentMetrics: async (params) => api.get('/recruitment/metrics', { params }),
    getPipeline: async (jobId) => api.get(`/recruitment/jobs/${jobId}/pipeline`)
  }
});

// HR Dashboard Service
const hrDashboardService = {
  getDashboard: async () => api.get('/hr/dashboard'),
  getHRConfiguration: async () => api.get('/hr/configuration'),
  updateHRConfiguration: async (config) => api.put('/hr/configuration', config),
  getDepartments: async () => api.get('/hr/departments')
};

// Utility functions
export const calculateLeaveDuration = (startDate, endDate, isHalfDay = false) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Reset time parts to compare dates only
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  
  // Calculate difference in days
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  
  // If it's a half day leave, return 0.5
  if (isHalfDay && diffDays === 1) {
    return 0.5;
  }
  
  return diffDays;
};

// ========================
// Public API - Maintaining backward compatibility
// ========================

const hrService = {
  // Employee Management
  getEmployees: async (params) => employeeServiceBase.getAll(params),
  getEmployee: async (id) => employeeServiceBase.getById(id),
  createEmployee: async (data) => employeeServiceBase.create(data),
  updateEmployee: async (id, data) => employeeServiceBase.update(id, data),
  deleteEmployee: async (id) => employeeServiceBase.delete(id),
  uploadEmployeeDocument: async (id, file, metadata) => {
    return api.uploadFile(
      `/hr/employees/${id}/documents`, 
      file, 
      'documents', 
      metadata || {}
    );
  },
  getEmployeeDocuments: async (id) => employeeServiceBase.getDocuments(id),
  downloadEmployeeDocument: async (employeeId, documentId) => 
    api.get(`/hr/employees/${employeeId}/documents/${documentId}/download`),
  deleteEmployeeDocument: async (employeeId, documentId) => 
    api.delete(`/hr/employees/${employeeId}/documents/${documentId}`),
  exportEmployees: async (params) => employeeServiceBase.exportEmployees(params),
  importEmployees: async (file) => employeeServiceBase.importEmployees(file),
  bulkUpdateEmployees: async (updates) => employeeServiceBase.bulkUpdate(updates),
  getOrgHierarchy: async () => employeeServiceBase.getOrgHierarchy(),
  getOrgChart: async () => api.get('/org-chart/data'),
  assignManager: async (employeeId, managerId) => api.post('/org-chart/assign-manager', { employeeId, managerId }),
  getPotentialManagers: async (employeeId, params = {}) => api.get(`/org-chart/employee/${employeeId}/potential-managers`, { params }),
  
  // Leave Management
  getLeaves: async (params) => leaveServiceBase.getAll(params),
  getLeave: async (id) => leaveServiceBase.getById(id),
  createLeaveRequest: async (data) => leaveServiceBase.create(data),
  updateLeaveRequest: async (id, data) => leaveServiceBase.update(id, data),
  deleteLeaveRequest: async (id) => leaveServiceBase.delete(id),
  submitLeave: async (id) => leaveServiceBase.submitLeave(id),
  approveLeave: async (id, data) => leaveServiceBase.approveLeave(id, data),
  rejectLeave: async (id, data) => leaveServiceBase.rejectLeave(id, data),
  cancelLeave: async (id, data) => leaveServiceBase.cancelLeave(id, data),
  getLeaveBalance: async (employeeId) => leaveServiceBase.getLeaveBalance(employeeId),
  getMyLeaveBalance: async () => leaveServiceBase.getMyLeaveBalance(),
  updateLeaveBalance: async (employeeId, data) => leaveServiceBase.updateLeaveBalance(employeeId, data),
  getMyLeaves: async (params) => leaveServiceBase.getMyLeaves(params),
  createMyLeaveRequest: async (data) => leaveServiceBase.createMyLeaveRequest(data),
  getLeaveTypes: async () => leaveServiceBase.getLeaveTypes(),
  getLeaveCalendar: async (params) => leaveServiceBase.getLeaveCalendar(params),
  getPendingApprovals: async () => leaveServiceBase.getPendingApprovals(),
  
  // Attendance Management
  getAttendance: async (params) => attendanceServiceBase.getAll(params),
  getAttendanceRecord: async (id) => attendanceServiceBase.getById(id),
  createAttendanceRecord: async (data) => attendanceServiceBase.create(data),
  updateAttendanceRecord: async (id, data) => attendanceServiceBase.update(id, data),
  deleteAttendanceRecord: async (id) => attendanceServiceBase.delete(id),
  getCurrentAttendanceStatus: async () => attendanceServiceBase.getCurrentStatus(),
  clockIn: async (data) => attendanceServiceBase.clockIn(data),
  clockOut: async (data) => attendanceServiceBase.clockOut(data),
  startBreak: async (data) => attendanceServiceBase.startBreak(data),
  endBreak: async (data) => attendanceServiceBase.endBreak(data),
  getMyAttendance: async (params) => attendanceServiceBase.getMyAttendance(params),
  getMyAttendanceSummary: async (params) => attendanceServiceBase.getMyAttendanceSummary(params),
  getMyTimesheet: async (params) => attendanceServiceBase.getMyTimesheet(params),
  submitTimesheet: async (data) => attendanceServiceBase.submitTimesheet(data),
  getTodayOverview: async () => attendanceServiceBase.getTodayOverview(),
  getDailyReport: async (params) => attendanceServiceBase.getDailyReport(params),
  getMonthlyReport: async (params) => attendanceServiceBase.getMonthlyReport(params),
  getTeamAttendance: async (params) => attendanceServiceBase.getTeamAttendance(params),
  exportAttendance: async (params) => attendanceServiceBase.exportAttendance(params),
  bulkClockIn: async (data) => attendanceServiceBase.bulkClockIn(data),
  
  // Performance Management
  getPerformanceReviews: async (params) => performanceServiceBase.getAll(params),
  getPerformanceReview: async (id) => performanceServiceBase.getById(id),
  createPerformanceReview: async (data) => performanceServiceBase.create(data),
  updatePerformanceReview: async (id, data) => performanceServiceBase.update(id, data),
  deletePerformanceReview: async (id) => performanceServiceBase.delete(id),
  submitSelfReview: async (id, data) => performanceServiceBase.submitSelfReview(id, data),
  submitManagerReview: async (id, data) => performanceServiceBase.submitManagerReview(id, data),
  completeReview: async (id) => performanceServiceBase.completeReview(id),
  acknowledgeReview: async (id) => performanceServiceBase.acknowledgeReview(id),
  getGoals: async (params) => performanceServiceBase.getGoals(params),
  getGoal: async (id) => performanceServiceBase.getGoal(id),
  createGoal: async (data) => performanceServiceBase.createGoal(data),
  updateGoal: async (id, data) => performanceServiceBase.updateGoal(id, data),
  updateGoalProgress: async (id, progress) => performanceServiceBase.updateGoalProgress(id, progress),
  getPerformanceAnalytics: async (params) => performanceServiceBase.getPerformanceAnalytics(params),
  getReviewTemplates: async () => performanceServiceBase.getReviewTemplates(),
  getReviewCycles: async () => performanceServiceBase.getReviewCycles(),
  
  // Recruitment Management
  getJobs: async (params) => recruitmentServiceBase.getAll(params),
  getJob: async (id) => recruitmentServiceBase.getById(id),
  createJob: async (data) => recruitmentServiceBase.create(data),
  updateJob: async (id, data) => recruitmentServiceBase.update(id, data),
  deleteJob: async (id) => recruitmentServiceBase.delete(id),
  publishJob: async (id) => recruitmentServiceBase.publishJob(id),
  archiveJob: async (id) => recruitmentServiceBase.archiveJob(id),
  getCandidates: async (params) => recruitmentServiceBase.getCandidates(params),
  getCandidate: async (id) => recruitmentServiceBase.getCandidate(id),
  createCandidate: async (data) => recruitmentServiceBase.createCandidate(data),
  updateCandidate: async (id, data) => recruitmentServiceBase.updateCandidate(id, data),
  updateApplicationStatus: async (candidateId, jobId, status) => 
    recruitmentServiceBase.updateApplicationStatus(candidateId, jobId, status),
  scheduleInterview: async (data) => recruitmentServiceBase.scheduleInterview(data),
  createOffer: async (data) => recruitmentServiceBase.createOffer(data),
  getRecruitmentMetrics: async (params) => recruitmentServiceBase.getRecruitmentMetrics(params),
  
  // HR Dashboard
  getHRDashboard: async () => hrDashboardService.getDashboard(),
  getHRConfiguration: async () => hrDashboardService.getHRConfiguration(),
  updateHRConfiguration: async (config) => hrDashboardService.updateHRConfiguration(config),
  getDepartments: async () => hrDashboardService.getDepartments(),
  
  // Alias for getJobs (used by hrStore)
  getJobPostings: async (params) => recruitmentServiceBase.getAll(params)
};

// Export individual service bases for direct usage if needed
export const employeeService = employeeServiceBase;
export const leaveService = leaveServiceBase;
export const attendanceService = attendanceServiceBase;
export const performanceService = performanceServiceBase;
export const recruitmentService = recruitmentServiceBase;

// Export the main HR service
export default hrService;